import { useAuth } from "@babackapp/auth-client/hooks";
import { useFormErrorNotification } from "@babackapp/react-hook-forms-components/form-notifications";
import useTranslation, {
  Translation,
} from "@babackapp/translations/use-translation";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@babackapp/ui/components/accordion";
import { Badge } from "@babackapp/ui/components/badge";
import { Button } from "@babackapp/ui/components/button";
import { Checkbox } from "@babackapp/ui/components/checkbox";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@babackapp/ui/components/dialog";
import { LoadingButton } from "@babackapp/ui/components/loading-button";
import { cn } from "@babackapp/ui/lib/utils";
import NumberFlow from "@number-flow/react";
import { PropsWithChildren, useCallback, useState } from "react";
import { useDownloadReturnRequests } from "../../../hooks/use-download-return-requests";
import { useExportReturnFieldsContext } from "../../../hooks/use-export-return-fields-context";
import {
  exportReturnFieldsBySections,
  FieldsSections,
  SectionKeys,
} from "../../../utils/selectable-expot-fields";

type Props = PropsWithChildren<{
  open: boolean;
  openChange: (open: boolean) => void;
}>;
export const SelectExportReturnFieldDialog = ({
  children,
  open,
  openChange,
}: Props) => {
  const errorNotification = useFormErrorNotification();
  const onError = useCallback(() => {
    setIsDownloadLoading(false);
    errorNotification();
  }, []);

  const onSuccess = useCallback(() => {
    setConfirmationOpen(true);
    setIsDownloadLoading(false);
    openChange(false);
  }, []);
  const download = useDownloadReturnRequests({ onSuccess, onError });
  const [isDownloadLoading, setIsDownloadLoading] = useState<boolean>(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const { user } = useAuth();

  const t = useTranslation();
  const { toggleSelectAll, areAllFieldsSelected, returnExportFields } =
    useExportReturnFieldsContext();

  const downloadResults = useCallback(() => {
    setIsDownloadLoading(true);
    const fieldsToDownload = Object.values(returnExportFields).flat();
    return download([...fieldsToDownload]);
  }, [returnExportFields, download]);

  return (
    <>
      <Dialog open={open} onOpenChange={openChange}>
        <DialogTrigger>{children}</DialogTrigger>
        <DialogContent className="max-w-xl overflow-y-scroll max-h-[calc(100vh-64px)] flex flex-col pb-0">
          <DialogHeader>
            <DialogTitle>
              <div className="flex flex-1 justify-between items-center">
                <div>{t("returns.CSVFields.title")}</div>
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground p-4">
                  <Checkbox
                    onCheckedChange={toggleSelectAll}
                    checked={areAllFieldsSelected()}
                  />
                  {t("returns.CSVFields.selectAll")}
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>
          <Accordion type="multiple">
            {exportReturnFieldsBySections.map((section) => (
              <SelectFieldsCathegory
                key={section.titleKey}
                titleKey={section.titleKey}
                fields={section.fields}
              />
            ))}
          </Accordion>
          <div className="flex items-center justify-center gap-4 sticky bottom-0 py-5 mt-5 bg-gradient-to-b from-white/0 to-white">
            {isDownloadLoading ? (
              <LoadingButton isLoading={isDownloadLoading} variant="link" />
            ) : (
              <Button onClick={downloadResults} variant="default">
                {t("returns.downloadcsv")}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={confirmationOpen} onOpenChange={setConfirmationOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("returns.generatingExport")}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {`${t("returns.generatingExportDescription")} ${user?.emails[0].address}`}
          </div>
          <DialogFooter>
            <DialogClose>
              <Button type="button">{t("misc.close")}</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const SelectExportReturnField = ({
  field,
  OnCheckChange,
}: {
  field: FieldsSections;
  OnCheckChange: (field: FieldsSections) => void;
}) => {
  const t = useTranslation();
  const { isFieldSelected } = useExportReturnFieldsContext();

  return (
    <div className="flex justify-between p-1">
      <div className="flex items-center gap-4">
        <Checkbox
          checked={isFieldSelected(field)}
          onCheckedChange={() => OnCheckChange(field)}
        />
        <div className="text-sm font-medium text-muted-foreground inline-flex items-center gap-2">
          {t(`returns.CSVFields.${field}` as Translation)}
        </div>
      </div>
    </div>
  );
};

const SelectFieldsCathegory = ({
  titleKey,
  fields,
}: {
  titleKey: SectionKeys;
  fields: FieldsSections[];
}) => {
  const t = useTranslation();
  const { sectionState, isCategorySelected } = useExportReturnFieldsContext();

  const {
    selectedFieldsCount,
    toggleAllFields,
    toggleField,
    maxFields,
    isAnyFieldSelected,
  } = sectionState(titleKey);

  return (
    <AccordionItem value={titleKey}>
      <div className="flex items-center">
        <div className="  flex items-start w-full gap-2 align-text-top">
          <Checkbox
            className={cn(
              "p-0 m-0",
              isAnyFieldSelected ? "border-primary" : "border-muted-foreground",
            )}
            onCheckedChange={toggleAllFields}
            checked={isCategorySelected(titleKey)}
          />
          <span className="text-sm font-medium text-muted-foreground align-text-top">
            {t(`returns.CSVFields.${titleKey}` as Translation)}
          </span>
        </div>
        <AccordionTrigger>
          <Badge variant="secondary" className="ml-2">
            <NumberFlow
              value={selectedFieldsCount}
              suffix={` / ${maxFields}`}
            />
          </Badge>
        </AccordionTrigger>
      </div>
      <AccordionContent>
        <div className="border-border border rounded-md flex-col gap-4 divide-y">
          {fields.map((field) => (
            <SelectExportReturnField
              key={field}
              field={field}
              OnCheckChange={toggleField}
            />
          ))}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};
